import React, { createContext, startTransition, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getStoreProductsPage, type StorefrontProduct } from '../../api/client';

// The store's own products in catalog order, for the blocks that take one by their position when no
// product was chosen for them — a bundle's three slots, a showcase's slides. Read once per page and shared,
// so a page of ten such blocks costs one request rather than ten, and only when a block asks for it.

/** How many products a page's blocks can take by position. */
const SLOTS = 24;

/** The store's products as a block reads them. */
export type ProductSlots = { status: 'loading' | 'ready' | 'failed'; data: StorefrontProduct[] };

/** Starts the page's one read, or hands back the one already started. */
const SlotsContext = createContext<(() => Promise<StorefrontProduct[]>) | null>(null);

// Reads the store's first products.
const readSlots = (): Promise<StorefrontProduct[]> => getStoreProductsPage(SLOTS).then((page) => page.items);

// Shares one lazy read of the store's products with every block on the page.
export const ProductSlotsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pending = useRef<Promise<StorefrontProduct[]> | null>(null);
  const request = useCallback(() => (pending.current ??= readSlots()), []);
  return <SlotsContext.Provider value={request}>{children}</SlotsContext.Provider>;
};

// Reads the store's products by position, through the page's shared read when there is one.
export function useProductSlots(wanted: boolean): ProductSlots {
  const request = useContext(SlotsContext);
  const [state, setState] = useState<ProductSlots>({ status: 'loading', data: [] });
  useEffect(() => {
    if (!wanted) return undefined;
    let live = true;
    (request ?? readSlots)()
      .then((data) => { if (live) startTransition(() => setState({ status: 'ready', data })); })
      .catch(() => { if (live) setState({ status: 'failed', data: [] }); });
    return () => { live = false; };
  }, [request, wanted]);
  return state;
}
